import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { getAuth, Unsubscribe } from "firebase/auth";
import { useDialog } from "app/hook";
import { AppDispatch } from "app/store";
import { parentsFirebase } from "app/Auth";
import { DialogType, ParentClaimStatus } from "app/types";
import { getEnv, actions as configActions } from "app/configSlice";
import parentI18n from "features/parent/i18n";
import { createParentAccount } from "features/user/api";
import { mapLocaleToLanguage } from "./useAuthLocale";

const toClaimStatus = (status?: string): ParentClaimStatus | null => {
  switch (status) {
    case "claimed":
      return ParentClaimStatus.CLAIMED;
    case "already_owned":
      return ParentClaimStatus.ALREADY_OWNED;
    case "quota_exceeded":
      return ParentClaimStatus.QUOTA_EXCEEDED;
    case "skipped":
      return ParentClaimStatus.SKIPPED;
    default:
      return null;
  }
};

export const useSignUp = () => {
  const handleDialog = useDialog();
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    let unsubscribe: Unsubscribe;

    try {
      const childrenAuth = getAuth();
      const auth = getAuth(parentsFirebase);
      unsubscribe = auth.beforeAuthStateChanged(async (user) => {
        try {
          if (user) {
            if (!user.isAnonymous) {
              let email: string;
              if (
                user.providerData[0].providerId === "google.com" ||
                user.providerData[0].providerId === "apple.com"
              ) {
                email = user.providerData[0].email;
              } else {
                email = user.email;
              }
              const response = await createParentAccount({
                user: {
                  uid: user.uid,
                  child_uid: childrenAuth.currentUser?.uid,
                  email: email,
                  provider: user.providerData[0].providerId,
                  display_name: parentI18n.t("MSG_PARENTS_PROFILE_DEFAULT_NAME"),
                  language: mapLocaleToLanguage(parentI18n.locale),
                  env: getEnv(),
                  timezone: "Asia/Tokyo",
                  country: "ja",
                },
              });
              dispatch(
                configActions.setLastClaimStatus(toClaimStatus(response.data?.claim?.status)),
              );
              if (childrenAuth.currentUser?.uid) {
                await childrenAuth.signOut();
              }
            }
          }

          unsubscribe();
        } catch (error) {
          console.error(error);
          unsubscribe();
          // エラーダイアログを最前面ローディングが覆わないよう先にクリア
          handleDialog({ type: DialogType.LOADING, value: false });
          handleDialog({ type: DialogType.ERROR_NETWORK, value: true });
        }
      });
    } catch (error) {
      console.error(error);
      handleDialog({ type: DialogType.ERROR_NETWORK, value: true });
    }
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
